import { SMS, Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Thread as UnifiedThread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { CachedResourceType, CachedEndpointData, QueryResourceIndexes, ResourceMatchingOptions } from '../types';
import {
  combinedSMSMatchFn,
  combinedThreadMatchFn,
  combinedUnifiedThreadMatchFn,
} from './combined-resource-match-functions';

type GetListThreadsResourceIndexesArgs<ResourceType extends CachedResourceType> = {
  data: CachedEndpointData<'ListThreads'>;
} & Omit<ResourceMatchingOptions<ResourceType>, 'updater'>;

type GetUnifiedListThreadsResourceIndexesArgs<ResourceType extends CachedResourceType> = {
  data: CachedEndpointData<'ListUnifiedThreads'>;
} & Omit<ResourceMatchingOptions<ResourceType>, 'updater'>;

/**
 * Finds indexes of an SMS resource within ListThreads cached data
 *
 * @param data - ListThreads endpoint cached data
 * @param matchValues - Values to match the SMS resource
 * @param matchFn - Function to match the specific SMS resource
 * @param matchStrategy - Strategy to match the resource (default: 'and')
 * @returns Object containing pageIndex, threadIndex, and smsIndex of the found resource
 */
export const getListThreadsSMSResourceIndexes = ({
  data,
  matchValues,
  matchFn,
  matchStrategy = 'and',
}: GetListThreadsResourceIndexesArgs<SMS>): QueryResourceIndexes<'ListThreads'> => {
  const originalResourceIndexes: QueryResourceIndexes<'ListThreads'> = {};
  data.pages.forEach((page, pageIndex) => {
    if (originalResourceIndexes.pageIndex !== undefined) {
      return;
    }

    const threadIndex = page.threads.findIndex((thread) => thread.id === matchValues.threadId);
    const smsIndex =
      threadIndex === -1
        ? undefined
        : page.threads[threadIndex]?.messages.findIndex(combinedSMSMatchFn({ matchValues, matchFn, matchStrategy }));

    if (threadIndex !== -1) {
      originalResourceIndexes.pageIndex = pageIndex;
      originalResourceIndexes.threadIndex = threadIndex;
      originalResourceIndexes.smsIndex = smsIndex;
    }
  });

  return originalResourceIndexes;
};

/**
 * Finds indexes of an Thread resource within ListThreads cached data
 *
 * @param data - ListThreads endpoint cached data
 * @param matchValues - Values to match the Thread resource
 * @param matchFn - Function to match the specific Thread resource
 * @returns Object containing pageIndex and threadIndex the found resource
 */
export const getListThreadsThreadResourceIndexes = ({
  data,
  matchValues,
  matchFn,
  matchStrategy = 'and',
}: Omit<GetListThreadsResourceIndexesArgs<Thread>, 'updater'>): QueryResourceIndexes<'ListThreads'> => {
  const originalResourceIndexes: QueryResourceIndexes<'ListThreads'> = {};
  data.pages.forEach((page, pageIndex) => {
    if (originalResourceIndexes.pageIndex !== undefined) {
      return;
    }

    const threadIndex = page.threads.findIndex(combinedThreadMatchFn({ matchValues, matchFn, matchStrategy }));

    if (threadIndex !== -1) {
      originalResourceIndexes.pageIndex = pageIndex;
      originalResourceIndexes.threadIndex = threadIndex;
    }
  });

  return originalResourceIndexes;
};

export const getListUnifiedThreadsResourceIndexes = ({
  data,
  matchValues,
  matchFn,
  matchStrategy = 'and',
}: Omit<
  GetUnifiedListThreadsResourceIndexesArgs<UnifiedThread>,
  'updater'
>): QueryResourceIndexes<'ListUnifiedThreads'> => {
  const matchFunction = combinedUnifiedThreadMatchFn({ matchValues, matchFn, matchStrategy });

  for (let pageIndex = 0; pageIndex < data.pages.length; pageIndex++) {
    const page = data.pages[pageIndex];
    if (!page) continue;

    for (let threadIndex = 0; threadIndex < page.threads.length; threadIndex++) {
      const thread = page.threads[threadIndex];
      if (!thread) continue;

      if (matchFunction(thread)) {
        return { pageIndex, threadIndex };
      }
    }
  }

  return { pageIndex: -1, threadIndex: -1 };
};

type ListThreadsDataFromIndexes = {
  page?: CachedEndpointData<'ListThreads'>['pages'][number];
  thread?: Thread;
  sms?: SMS;
};

/**
 * Retrieves the corresponding page, thread, and SMS data from ListThreads cached data using provided indexes
 *
 * @param originalData - The complete ListThreads endpoint cached data
 * @param indexes - Object containing pageIndex, threadIndex, and smsIndex to locate the data
 * @returns Object containing the retrieved page, thread, and SMS data if they exist
 */
export const getListThreadsDataFromIndexes = (
  originalData: CachedEndpointData<'ListThreads'>,
  { pageIndex, threadIndex, smsIndex }: QueryResourceIndexes<'ListThreads'>
): ListThreadsDataFromIndexes => {
  const hasPageIndex = pageIndex !== undefined && pageIndex !== -1;
  if (!hasPageIndex) {
    return {};
  }

  const hasThreadIndex = threadIndex !== undefined && threadIndex !== -1;
  const hasSMSIndex = smsIndex !== undefined && smsIndex !== -1;
  return {
    page: originalData.pages[pageIndex],
    thread: hasThreadIndex ? originalData.pages[pageIndex]?.threads[threadIndex] : undefined,
    sms:
      hasThreadIndex && hasSMSIndex
        ? originalData.pages[pageIndex]?.threads[threadIndex]?.messages[smsIndex]
        : undefined,
  };
};

type GetThreadDetailsResourceIndexesArgs<ResourceType extends CachedResourceType> = {
  data: CachedEndpointData<'GetThreadDetails'>;
} & Omit<ResourceMatchingOptions<ResourceType>, 'updater'>;

/**
 * Finds indexes of an SMS resource within GetThreadDetails cached data
 *
 * @param data - GetThreadDetails endpoint cached data
 * @param matchValues - Values to match the SMS resource
 * @param matchFn - Function to match the specific SMS resource
 * @param matchStrategy - Strategy to match the resource (default: 'and')
 * @returns Object containing the smsIndex of the found resource
 */
export const getGetThreadDetailsSMSResourceIndexes = ({
  data,
  matchValues,
  matchFn,
  matchStrategy = 'and',
}: GetThreadDetailsResourceIndexesArgs<SMS>): QueryResourceIndexes<'GetThreadDetails'> => {
  const originalResourceIndexes: QueryResourceIndexes<'GetThreadDetails'> = {};

  if (!data?.thread) {
    return originalResourceIndexes;
  }

  const smsIndex = data.thread.messages.findIndex(combinedSMSMatchFn({ matchValues, matchFn, matchStrategy }));

  if (smsIndex !== -1) {
    originalResourceIndexes.smsIndex = smsIndex;
  }

  return originalResourceIndexes;
};

type ThreadDetailsDataFromIndexes = {
  thread?: Thread;
  sms?: SMS;
};

/**
 * Retrieves the corresponding thread and SMS data from GetThreadDetails cached data using provided indexes
 *
 * @param originalData - The complete GetThreadDetails endpoint cached data
 * @param indexes - Object containing smsIndex to locate the SMS data
 * @returns Object containing the retrieved thread and SMS data if they exist
 */
export const getGetThreadDetailsDataFromIndexes = (
  originalData: CachedEndpointData<'GetThreadDetails'>,
  { smsIndex }: QueryResourceIndexes<'GetThreadDetails'>
): ThreadDetailsDataFromIndexes => {
  if (!originalData?.thread) {
    return {};
  }

  const hasSMSIndex = smsIndex !== undefined && smsIndex !== -1;

  return {
    thread: originalData.thread,
    sms: hasSMSIndex ? originalData.thread.messages[smsIndex] : undefined,
  };
};
